import { useEffect, useRef, useState } from "react";

/**
 * Keep a kiosk result on screen for `durationMs`, then call `onDone` once.
 * `resultKey` identifies the result being shown (its scan id); a new key
 * restarts the clock and null means nothing is showing. Returns the whole
 * seconds left for "Back to ready in 2 s".
 */
export function useResultTimeout(
  resultKey: string | null,
  durationMs: number,
  onDone: () => void,
): number {
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(durationMs / 1000));

  useEffect(() => {
    if (resultKey === null) return;
    const shownAt = Date.now();
    setSecondsLeft(Math.ceil(durationMs / 1000));
    const tick = window.setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((durationMs - (Date.now() - shownAt)) / 1000)));
    }, 250);
    const done = window.setTimeout(() => onDoneRef.current(), durationMs);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(done);
    };
  }, [durationMs, resultKey]);

  return secondsLeft;
}
